/**
 * The padstack, drawn as connectivity rather than as a list of layers.
 *
 * A column stands for the board seen edge-on, top layer at the top, each copper
 * layer a band in its own colour. The pad is a barrel through the column where
 * it has copper. Where something actually *touches* it, a spur runs out of the
 * barrel to the net it joins.
 *
 * The distinction is the whole point. A via passes through a ground plane and
 * is joined to it only if the pour reaches it — a plane with a clearance round
 * the via is copper the via goes through and is not connected to. A list of
 * occupied layers says "on all four" either way and is worth nothing; this says
 * "on all four, joined on two, and here is which nets".
 *
 * A layer the pad is present on but joined to nothing gets a hollow stub and
 * the word "no connection", because that is the finding: a via that reaches a
 * plane it never touches is usually a mistake, and it is invisible on the
 * drawing.
 */

import type { CadDocument, ComponentEntity, PadEntity } from '@sircad/core/model.ts';
import { padStack, type StackLayer } from '@sircad/core/net/padstack.ts';

export interface StackView {
  el: HTMLCanvasElement;
  update(): void;
}

const INK = {
  ground: '#101418',
  substrate: '#2a2f36',
  barrel: '#d9a441',
  hollow: '#5b6570',
  spur: '#8fb8e0',
  label: '#e8edf2',
  dim: '#7d858c',
  none: '#a8636a',
};

/** Where the column sits, and how wide the barrel through it is. */
const COL_X = 104;
const COL_W = 30;
const BARREL = 11;

export function padStackView(
  pad: PadEntity, doc: CadDocument, owner: ComponentEntity | null = null,
): StackView {
  const el = document.createElement('canvas');
  el.className = 'edit-stack';
  el.width = 260;

  const update = (): void => {
    const rows: StackLayer[] = padStack(doc, pad, owner);
    // Sized to what there is: a two-layer board should not carry the whitespace
    // of a six-layer one.
    el.height = Math.max(72, 18 + rows.length * 26);

    const ctx = el.getContext('2d');
    if (!ctx) return;
    const w = el.width;
    const h = el.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = INK.ground;
    ctx.fillRect(0, 0, w, h);
    if (rows.length === 0) return;

    ctx.font = '10px ui-monospace, monospace';
    ctx.textBaseline = 'middle';

    const band = 18;
    const step = 26;
    const top = 12;
    const yOf = (n: number): number => top + n * step + band / 2;

    // The substrate: one column behind everything, so the layers read as a
    // board rather than as separate bars.
    ctx.fillStyle = INK.substrate;
    ctx.fillRect(COL_X, top - 4, COL_W, rows.length * step + 8 - (step - band));

    rows.forEach((r, n) => {
      const y = yOf(n);

      // The layer itself, in its own colour, across the column.
      ctx.fillStyle = `#${(r.colour >>> 0).toString(16).padStart(6, '0')}`;
      ctx.fillRect(COL_X, y - band / 2, COL_W, band);

      // Its name, to the left.
      ctx.fillStyle = r.present ? INK.label : INK.dim;
      ctx.textAlign = 'right';
      ctx.fillText(r.name.slice(0, 14), COL_X - 10, y);

      // The barrel: the pad's own copper punched through this layer. Filled
      // where it conducts, hollow where it merely passes through.
      const joined = r.touching > 0;
      const bx = COL_X + COL_W / 2 - BARREL / 2;
      if (r.present) {
        ctx.fillStyle = joined ? INK.barrel : INK.ground;
        ctx.fillRect(bx, y - band / 2, BARREL, band);
        ctx.strokeStyle = joined ? INK.barrel : INK.hollow;
        ctx.lineWidth = 1;
        ctx.strokeRect(bx + 0.5, y - band / 2 + 0.5, BARREL - 1, band - 1);
      }

      // The spur, and what it joins.
      ctx.textAlign = 'left';
      if (!r.present) {
        ctx.fillStyle = INK.dim;
        ctx.fillText('—', COL_X + COL_W + 12, y);
        return;
      }
      if (!joined) {
        ctx.fillStyle = INK.none;
        ctx.fillText('no connection', COL_X + COL_W + 12, y);
        return;
      }

      ctx.strokeStyle = INK.spur;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(COL_X + COL_W, y);
      ctx.lineTo(COL_X + COL_W + 9, y);
      ctx.stroke();
      // A small node where the spur leaves the barrel, so a branch reads as a
      // junction rather than as a tick.
      ctx.fillStyle = INK.spur;
      ctx.beginPath();
      ctx.arc(COL_X + COL_W + 9, y, 2.5, 0, Math.PI * 2);
      ctx.fill();

      const net = r.nets.length === 0 ? '(unnamed)'
        : r.nets.length === 1 ? r.nets[0]!
          : `${r.nets[0]} +${r.nets.length - 1}`;
      ctx.fillStyle = r.nets.length > 1 ? INK.none : INK.label;
      ctx.fillText(net.slice(0, 14), COL_X + COL_W + 16, y);

      // How many pieces of copper meet here, when it is more than one — the
      // difference between a via a track arrives at and one a plane surrounds.
      const room = w - (COL_X + COL_W + 16);
      if (room > 90) {
        ctx.fillStyle = INK.dim;
        ctx.textAlign = 'right';
        ctx.fillText(`${r.touching}`, w - 8, y);
        ctx.textAlign = 'left';
      }
    });
  };

  update();
  return { el, update };
}
